/**
 * Camera configuration schemas
 */

import { z } from "zod";
import { Vector3Schema } from "./scene.schema";

/**
 * Base camera configuration
 */
const BaseCameraConfigSchema = z.object({
  attachControls: z.boolean().optional(),
  fov: z.number().positive().optional(),
  maxZ: z.number().positive().optional(),
  minZ: z.number().positive().optional(),
  name: z.string(),
  position: Vector3Schema.optional(),
});

/**
 * Arc rotate camera configuration
 */
export const ArcRotateCameraConfigSchema = BaseCameraConfigSchema.extend({
  alpha: z.number().optional(),
  beta: z.number().optional(),
  lowerAlphaLimit: z.number().optional(),
  lowerBetaLimit: z.number().optional(),
  lowerRadiusLimit: z.number().optional(),
  radius: z.number().positive().optional(),
  target: Vector3Schema.optional(),
  type: z.literal("arcRotate"),
  upperAlphaLimit: z.number().optional(),
  upperBetaLimit: z.number().optional(),
  upperRadiusLimit: z.number().optional(),
});

/**
 * Universal camera configuration
 */
export const UniversalCameraConfigSchema = BaseCameraConfigSchema.extend({
  angularSensibility: z.number().optional(),
  applyGravity: z.boolean().optional(),
  checkCollisions: z.boolean().optional(),
  speed: z.number().optional(),
  target: Vector3Schema.optional(),
  type: z.literal("universal"),
});

/**
 * Free camera configuration
 */
export const FreeCameraConfigSchema = BaseCameraConfigSchema.extend({
  angularSensibility: z.number().optional(),
  applyGravity: z.boolean().optional(),
  checkCollisions: z.boolean().optional(),
  speed: z.number().optional(),
  target: Vector3Schema.optional(),
  type: z.literal("free"),
});

/**
 * Follow camera configuration
 */
const FollowCameraConfigSchema = BaseCameraConfigSchema.extend({
  cameraAcceleration: z.number().optional(),
  heightOffset: z.number().optional(),
  maxCameraSpeed: z.number().optional(),
  radius: z.number().positive().optional(),
  rotationOffset: z.number().optional(),
  type: z.literal("followCamera"),
});

/**
 * Camera configuration discriminated union
 */
export const CameraConfigSchema = z.discriminatedUnion("type", [
  ArcRotateCameraConfigSchema,
  UniversalCameraConfigSchema,
  FreeCameraConfigSchema,
  FollowCameraConfigSchema,
]);

/**
 * Inferred types
 */
export type ArcRotateCameraConfig = z.infer<typeof ArcRotateCameraConfigSchema>;
export type UniversalCameraConfig = z.infer<typeof UniversalCameraConfigSchema>;
export type FreeCameraConfig = z.infer<typeof FreeCameraConfigSchema>;
export type CameraConfig = z.infer<typeof CameraConfigSchema>;

/**
 * Type guards
 */
export function isArcRotateCameraConfig(
  config: CameraConfig,
): config is ArcRotateCameraConfig {
  return config.type === "arcRotate";
}

export function isUniversalCameraConfig(
  config: CameraConfig,
): config is UniversalCameraConfig {
  return config.type === "universal";
}

export function isFreeCameraConfig(
  config: CameraConfig,
): config is FreeCameraConfig {
  return config.type === "free";
}

/**
 * Default configurations (separate from schemas as per CLAUDE.md)
 */
export const defaultArcRotateCameraConfig: ArcRotateCameraConfig = {
  alpha: -Math.PI / 2,
  attachControls: true,
  beta: Math.PI / 2.5,
  fov: 0.8,
  maxZ: 1000,
  minZ: 0.1,
  name: "ArcRotateCamera",
  radius: 10,
  target: { x: 0, y: 0, z: 0 },
  type: "arcRotate",
};

export const defaultUniversalCameraConfig: UniversalCameraConfig = {
  angularSensibility: 2000,
  applyGravity: false,
  attachControls: true,
  checkCollisions: false,
  fov: 0.8,
  maxZ: 1000,
  minZ: 0.1,
  name: "UniversalCamera",
  position: { x: 0, y: 5, z: -10 },
  speed: 0.5,
  target: { x: 0, y: 0, z: 0 },
  type: "universal",
};

export const defaultFreeCameraConfig: FreeCameraConfig = {
  angularSensibility: 2000,
  applyGravity: false,
  attachControls: true,
  checkCollisions: false,
  fov: 0.8,
  maxZ: 1000,
  minZ: 0.1,
  name: "FreeCamera",
  position: { x: 0, y: 5, z: -10 },
  speed: 0.5,
  target: { x: 0, y: 0, z: 0 },
  type: "free",
};

/**
 * Validation helpers
 */
export function validateCameraConfig(input: unknown): CameraConfig {
  return CameraConfigSchema.parse(input);
}

export function safeValidateCameraConfig(
  input: unknown,
):
  | { success: true; data: CameraConfig }
  | { success: false; error: z.ZodError } {
  const result = CameraConfigSchema.safeParse(input);
  if (result.success) {
    return { data: result.data, success: true };
  }
  return { error: result.error, success: false };
}
